import React from 'react';
import { FilterList } from '@material-ui/icons';
import { useTranslation } from '@fluentsms/i18n';
import {
  Badge,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  createStyles,
  Grid,
  IconButton,
  makeStyles,
  Popover,
  Theme,
  Tooltip,
} from '@material-ui/core';
import useDataFilterMenuState from './useDataFilterMenuState';
import { DataFilterMenuGroup } from './DataFilterMenuGroup';
import { ColumnFilter, Filter } from '../DataGridFilterMenu';
import { FilterColumns } from './types/filterTypes';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    headerIcon: {
      '&&': {
        padding: theme.spacing(1.5),
        fontSize: theme.spacing(3),
      },
    },
    cardActions: {
      float: 'right',
      '&& button': {
        marginLeft: theme.spacing(1),
      },
    },
    popover: {
      [theme.breakpoints.up('sm')]: {
        width: 695,
        maxWidth: '100%',
      },
    },
    filterContainer: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  }),
);

export type DataFilterMenuValue = {
  operator?: string;
  value?: string;
};

export type DataFilterMenuPropTypes<D extends Record<string, unknown>> = {
  columns: FilterColumns[];
  filters?: ColumnFilter[];
  onSubmit?: (allFilters: ColumnFilter[]) => void;
  onReset?: () => void;
};

export function FilterMenu<T extends Record<string, unknown>>({
  columns,
  filters = [],
  onSubmit = () => undefined,
  onReset = () => undefined,
}: DataFilterMenuPropTypes<T>): JSX.Element {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const { t }: { t: any } = useTranslation('componentLibrary'); // eslint-disable-line @typescript-eslint/no-explicit-any
  const classes = useStyles();
  const {
    addFilter,
    removeFilter,
    changeFilterColumn,
    changeFilterValue,
    reset,
    filterState,
    filterOrder,
    getColumnFilters,
    initialize,
  } = useDataFilterMenuState({
    filters,
  });

  const handleColumnChange = (oldFilterId: string, newColumnId: string) => {
    changeFilterColumn(oldFilterId, newColumnId);
  };

  const handleFilterChange = (filterId: string, filter: Filter) => {
    changeFilterValue(filterId, filter);
  };

  const handleReset = () => {
    reset();
    onReset();
    setAnchorEl(null);
  };

  const handleConfirm = () => {
    onSubmit(getColumnFilters());
    setAnchorEl(null);
  };

  const handleAddFilter = () => {
    addFilter();
  };

  const handleRemoveFilter = (filterId: string) => {
    removeFilter(filterId);
  };

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    initialize(filters);
    setAnchorEl(e.currentTarget);
  };

  const filterCount = filters.reduce((count: number, filter: ColumnFilter) => {
    count += filter?.value?.length || 0;
    return count;
  }, 0);

  return (
    <>
      <Tooltip title={t('filter')} arrow placement="top">
        <IconButton
          className={classes.headerIcon}
          size="medium"
          onClick={handleOpen}
          data-testid="data-filter-menu-open-menu-button"
        >
          <Badge
            badgeContent={filterCount}
            color="primary"
            invisible={!filterCount}
            data-testid="data-filter-menu-badge"
          >
            <FilterList />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        aria-labelledby={t('filterMenu.menuAriaLabel')}
        aria-describedby={t('filterMenu.menuAriaDescribedBy')}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Card raised className={classes.popover}>
          <CardHeader title={t('filters')} />
          <CardContent>
            <Grid container className={classes.filterContainer}>
              {filterOrder.map((filterId, filterListIndex) => {
                return (
                  <Grid container key={`filter-${filterId}-container`}>
                    <Grid item xs={10} sm={11}>
                      <DataFilterMenuGroup
                        filter={filterState[filterId]}
                        columns={columns}
                        key={`filter-${filterId}`}
                        filterId={filterId}
                        onColumnChange={handleColumnChange}
                        onFilterChange={handleFilterChange}
                        index={filterListIndex}
                        onAdd={handleAddFilter}
                        onRemove={() => handleRemoveFilter(filterId)}
                      />
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
          <CardActions className={classes.cardActions}>
            <Button
              onClick={handleReset}
              aria-label="data-filter-reset-button"
              data-testid="data-filter-reset-button"
              variant="outlined"
            >
              {t('reset')}
            </Button>
            <Button
              onClick={handleConfirm}
              color="primary"
              autoFocus
              aria-label="data-filter-submit-button"
              data-testid="data-filter-submit-button"
            >
              {t('filterMenu.applyFilters')}
            </Button>
          </CardActions>
        </Card>
      </Popover>
    </>
  );
}

export default FilterMenu;
